import {ArchiveRecord} from 'archive/ArchiveConstants';
import {MinimalFolderPermissions, Permissions} from 'auth/PermissionConstants';
import {MinimalSourceSummary, SourceSummary} from 'constants/SourceConstants';
import {EntitySummary} from 'constants/UserConstants';
import {MakeFieldsRequired} from 'util/Types';

export enum FolderSection {
  INVENTORY = 'inventory',
  NOTEBOOK = 'notebook',
  ALL = 'all',
}

const NOTEBOOK = 'NOTEBOOK';

export type FolderType = 'ALL' | 'NOTEBOOK' | 'INVENTORY';

// Keep in sync with Folder.Type
export const Type: Record<string, FolderType> = {
  ALL: 'ALL',
  ARBITRARY: NOTEBOOK, // when an arbitrary folder type is needed (post-inventory-and-notebook-merged world)
  INVENTORY: 'INVENTORY',
  NOTEBOOK,
};

export interface FolderSummary {
  archiveRecord?: ArchiveRecord | null;
  id: number;
  api_identifier: string;
  dbId: number;
  name: string;
  created_at: string;
  url: string;
  owner: EntitySummary;
  owner_handle: string;
  owner_url: string;
  description: string | null;
  owner_url_identifier: string;
  parent: FolderSummary | null;
  source?: SourceSummary;
  type: FolderType;
}

interface BaseFolder extends Omit<FolderSummary, 'id'> {
  modified_at?: string | null;
  permissions?: Permissions;
  // tagSchema is never present, but folders can be in collections of items with tag schemas.
  tagSchema?: undefined;
}

/**
 * This is the actual Folder to_dict returned from the server. It has a numeric ID, but this is deprecated and
 * we'd like to move it to a string ID. Consider converting to StringIDFolder instead.
 */
export interface NumberIDFolder extends BaseFolder {
  id: number;
}

/**
 * Code should try to use this interface instead of NumberIDFolder, since folders have API IDs and we'd prefer
 * to eventually switch folder.id to be an API ID instead of the DB ID.
 */
export interface StringIDFolder extends BaseFolder {
  id: string;
}

/**
 * For use cases that load folders in bulk, such as the folder selector, loading full model summaries is
 * very slow. A more minimalist model will be used in these cases that includes less data, but performs
 * significantly better.
 *
 */

export interface MinimalFolder {
  id: string;
  archiveRecord?: ArchiveRecord | null;
  api_identifier: string;
  name: string;
  owner_handle: string;
  parent: MinimalFolder | null;
  source?: MinimalSourceSummary;
  type: FolderType;
  permissions?: MinimalFolderPermissions;
}

export interface ServerMinimalFolder extends MinimalFolder {
  parentId: string | null;
}

export interface PagedMinimalFolder extends ServerMinimalFolder {
  hasChildren: boolean;
}

export interface FolderWithPermissions extends MakeFieldsRequired<NumberIDFolder, 'permissions'> {}

export interface FolderListingExtras {
  ancestralFolderIds: Array<string>;
}

export const FolderPreferences = {
  DEFAULT_FOLDER_NAMESPACE: 'default_folder.',
  DEFAULT_FOLDER_LIST_NAMESPACE: 'default_folder_list.',
  DEFAULT_SECTION: 'finder.default_section',
};
